import React from 'react'
import {
  CheckboxListFacet,
  RadioListFacet,
  PriceFacet,
  SizeFacet,
  ColorFacet,
  SortFacet,
  MultiplePriceFacet,
} from 'components/SA/SearchProvider'
import {
  FilterComponentParams,
  FilterCollapsableComponent,
  TransformFacetsToFiltersParams,
  CheckFacetsAllowedParams,
  AllowedValueObject,
  OrderAndIsOpenFiltersByHardcodedListParams,
  SAFilter,
  SAFilterObject,
  FacetsSpecialRulesParams,
  FacetOptionsFromProviderParams,
  MapLabelForFacetValuesParams,
} from '../types'
import { ALLOWED_CATEGORY_VALUES } from 'lib/constructor/constants/hardcodedSaatchiFilters'
import { slugify } from 'lib/slugify'
import { capitalizeString } from 'lib/helpers'

const filterComponent = ({
  inputType,
  facetName,
  options,
  nextAppRoute,
  baseUrl,
  facetService,
  extraData,
}: FilterComponentParams) => {
  switch (inputType) {
    case 'checkbox_price':
      return (
        <MultiplePriceFacet
          facetService={facetService}
          facetName={facetName}
          nextAppRoute={nextAppRoute}
          baseUrl={baseUrl}
          isCustomPriceEnabled={true}
          attributesList={extraData?.priceOptions}
          useReduxProviderHook={extraData?.useReduxProviderHook}
          customHandleUpdateSelectedFacet={extraData?.customHandleUpdateSelectedFacet}
          customClickFacetOption={extraData?.customClickFacetOption}
        />
      )

    case 'price':
      return (
        <PriceFacet
          facetService={facetService}
          facetName={facetName}
          nextAppRoute={nextAppRoute}
          baseUrl={baseUrl}
          isCustomPriceEnabled={true}
          attributesList={extraData?.priceOptions}
          useReduxProviderHook={extraData?.useReduxProviderHook}
          customHandleUpdateSelectedFacet={extraData?.customHandleUpdateSelectedFacet}
          customClickFacetOption={extraData?.customClickFacetOption}
        />
      )

    case 'size':
      return (
        <SizeFacet
          facetService={facetService}
          facetName={facetName}
          facetOptions={options}
          nextAppRoute={nextAppRoute}
          baseUrl={baseUrl}
          isCustomSizeEnabled={!extraData?.isPrintFacet}
          isPrintsFilter={extraData?.isPrintFacet}
          useReduxProviderHook={extraData?.useReduxProviderHook}
          customHandleUpdateSelectedFacet={extraData?.customHandleUpdateSelectedFacet}
          customClickFacetOption={extraData?.customClickFacetOption}
        />
      )

    case 'color':
      return (
        <ColorFacet
          facetService={facetService}
          facetName={facetName}
          facetOptions={options}
          nextAppRoute={nextAppRoute}
          baseUrl={baseUrl}
          useReduxProviderHook={extraData?.useReduxProviderHook}
          customHandleUpdateSelectedFacet={extraData?.customHandleUpdateSelectedFacet}
          customClickFacetOption={extraData?.customClickFacetOption}
        />
      )

    case 'toggle':
      return null

    case 'sort':
      return (
        <SortFacet
          facetService={facetService}
          facetName={facetName}
          facetOptions={options}
          nextAppRoute={nextAppRoute}
          baseUrl={baseUrl}
          onFilterSortOptions={extraData?.onFilterSortOptions}
          defaultValue={extraData?.defaultValue}
          useReduxProviderHook={extraData?.useReduxProviderHook}
          customHandleUpdateSelectedFacet={extraData?.customHandleUpdateSelectedFacet}
          customClickFacetOption={extraData?.customHandleUpdateSelectedFacet}
        />
      )

    case 'toggle':
      return null

    case 'radio':
      return (
        <RadioListFacet
          facetService={facetService}
          facetName={facetName}
          facetOptions={options}
          nextAppRoute={nextAppRoute}
          baseUrl={baseUrl}
          defaultValue={extraData?.defaultValue}
          useReduxProviderHook={extraData?.useReduxProviderHook}
          customHandleUpdateSelectedFacet={extraData?.customHandleUpdateSelectedFacet}
          customClickFacetOption={extraData?.customClickFacetOption}
        />
      )

    case 'checkbox':
    default:
      return (
        <CheckboxListFacet
          facetService={facetService}
          facetName={facetName}
          facetOptions={options}
          nextAppRoute={nextAppRoute}
          baseUrl={baseUrl}
          useReduxProviderHook={extraData?.useReduxProviderHook}
          customHandleUpdateSelectedFacet={extraData?.customHandleUpdateSelectedFacet}
          customClickFacetOption={extraData?.customClickFacetOption}
          searchFacetTextPlaceholder={extraData?.searchFacetTextPlaceholder}
        />
      )
  }
}

const mapLabelForFacetValues = ({ label, value }: MapLabelForFacetValuesParams): string => {
  const FACET_VALUES_TO_LABEL = {
    ['rag']: 'Fine Art Paper',
    ['photo']: 'Photo Paper',
    ['free_standing']: 'Free-Standing',
    ['wall_mounted']: 'Wall-Mounted',
  }

  return FACET_VALUES_TO_LABEL[value] || capitalizeString(label)
}

const facetOptionsFromProvider = ({
  facets,
  selectedCategory,
  hardcodedFacets,
  facetName,
  maxOptions = 100,
  selectedFacets,
}: FacetOptionsFromProviderParams): SAFilter => {
  const facetFromProviderToFilters = {}
  const isFacetByNameArray = Array.isArray(facets?.[facetName])
  const facetByNameArray = isFacetByNameArray ? facets?.[facetName] : []
  const hardcodedFacetByName =
    hardcodedFacets[facetName] || hardcodedFacets[`${selectedCategory}__${facetName}`]
  const selectedFacetValues = selectedFacets[facetName] || []
  const hasValuesSelectedInFacet = selectedFacetValues.length > 0

  if (Boolean(hardcodedFacetByName)) {
    const allowedValuesArray = hardcodedFacetByName.allowedValues.map(
      ({ value: allowedValue }) => allowedValue
    )

    const allowedOptions = facetByNameArray.reduce(
      (optionsAccumulator, { label, value, count }) => {
        // return only the first 100 valid options
        if (optionsAccumulator.length >= maxOptions) {
          return optionsAccumulator
        }

        const lowerCaseValue = value?.toLowerCase()
        // checking if the facet value from the provider is in the hardcoded array of allowedValues
        const isValueNotAllowed = !allowedValuesArray.includes(lowerCaseValue)
        // if facet value is not allowed, don't include it
        // also if useProviderValues is true, we want to skip this and add even if the value is not allowed
        if (isValueNotAllowed && !hardcodedFacetByName.useProviderValues) {
          return optionsAccumulator
        }

        // if it should return only facet values that has results, but the results for this facet value is 0, then don't include it
        if (hardcodedFacetByName.onlyNonEmptyResults && count === 0) {
          return optionsAccumulator
        }

        // other cases can include the facet value
        const isValueSelected = selectedFacetValues.includes(value)
        // If the value is selected, put at the top of the options list
        if (isValueSelected) {
          return [
            {
              label: mapLabelForFacetValues({ label, value }),
              value: lowerCaseValue,
            },
            ...optionsAccumulator,
          ]
        }

        return [
          ...optionsAccumulator,
          {
            label: mapLabelForFacetValues({ label, value }),
            value: lowerCaseValue,
          },
        ]
      },
      [] as ReadonlyArray<AllowedValueObject>
    )

    if (allowedOptions.length > 0) {
      facetFromProviderToFilters[facetName] = {
        displayName: hardcodedFacetByName.title,
        inputType: hardcodedFacetByName.inputType,
        options: allowedOptions,
        isOpen: hasValuesSelectedInFacet || hardcodedFacetByName.isOpen,
        isToggle: hardcodedFacetByName.isToggle,
      }
    }
  }

  return facetFromProviderToFilters
}

// Add special rules to format facets here
// In browse page get Mediums and Materials for first 100 results when selectedCategory = 'all'
const facetsSpecialRules = ({
  facets,
  hardcodedFacets,
  selectedCategory,
  mergedFacets,
  selectedFacets,
}: FacetsSpecialRulesParams): SAFilter => {
  const specialRulesFilter = Object.entries(facets).reduce(
    (specialFiltersAccumulator, [facetName]) => {
      const hardcodedFacetByName =
        hardcodedFacets[facetName] || hardcodedFacets[`${selectedCategory}__${facetName}`]

      if (Boolean(hardcodedFacetByName)) {
        if (hardcodedFacetByName?.extraData?.hasSpecialRule) {
          const specialFiltersByFacetName = facetOptionsFromProvider({
            facetName,
            facets,
            hardcodedFacets,
            selectedCategory,
            selectedFacets,
          })
          return { ...specialFiltersAccumulator, ...specialFiltersByFacetName }
        }
      }

      return specialFiltersAccumulator
    },
    {} as SAFilter
  )

  return { ...mergedFacets, ...specialRulesFilter }
}

export const checkFacetsAllowed = ({
  facets,
  hardcodedFacets,
  selectedCategory,
  selectedFacets,
}: CheckFacetsAllowedParams): SAFilter => {
  // Building the Filters from the response of Facets returned by the search provider and filtering the results by the hardcoded list
  const saFiltersAllowed = Object.entries(facets).reduce(
    (saFiltersAccumulator, [facetName, facetValues]) => {
      const isFacetValuesArray = Array.isArray(facetValues)
      const facetValuesArray = isFacetValuesArray ? facetValues : []
      const hardcodedFacetByName =
        hardcodedFacets[facetName] || hardcodedFacets[`${selectedCategory}__${facetName}`]
      const selectedFacetValues = selectedFacets[facetName] || []
      const hasValuesSelectedInFacet = selectedFacetValues.length > 0

      if (Boolean(hardcodedFacetByName)) {
        const allowedValuesArray = hardcodedFacetByName.allowedValues.map(
          ({ value: allowedValue }) => allowedValue
        )

        const allowedOptions = facetValuesArray.reduce(
          (optionsAccumulator, { label, value, count }) => {
            // checking if the facet value from the provider is in the hardcoded array of allowedValues
            const isValueNotAllowed = !allowedValuesArray.includes(value)
            // if facet value is not allowed, don't include it
            // also if useProviderValues is true, we want to skip this and add even if the value is not allowed
            if (isValueNotAllowed && !hardcodedFacetByName.useProviderValues) {
              return optionsAccumulator
            }

            // if it should return only facet values that has results, but the results for this facet value is 0, then don't include it
            if (hardcodedFacetByName.onlyNonEmptyResults && count === 0) {
              return optionsAccumulator
            }

            // other cases can include the facet value
            const isValueSelected = selectedFacetValues.includes(value)
            const hardcodedLabel =
              hardcodedFacetByName.allowedValues.find(
                ({ value: allowedValueToFilter }) => allowedValueToFilter === value
              )?.label || label
            // If the value is selected, put at the top of the options list
            if (isValueSelected) {
              return [
                {
                  label: mapLabelForFacetValues({ label: hardcodedLabel, value }),
                  value,
                },
                ...optionsAccumulator,
              ]
            }

            return [
              ...optionsAccumulator,
              {
                label: mapLabelForFacetValues({ label: hardcodedLabel, value }),
                value,
              },
            ]
          },
          [] as ReadonlyArray<AllowedValueObject>
        )

        if (allowedOptions.length > 0) {
          saFiltersAccumulator[facetName] = {
            displayName: hardcodedFacetByName.title,
            inputType: hardcodedFacetByName.inputType,
            options: allowedOptions,
            isOpen: hasValuesSelectedInFacet || hardcodedFacetByName.isOpen,
            isToggle: hardcodedFacetByName.isToggle,
            extraData: hardcodedFacetByName.extraData,
          }
        }
      }

      return saFiltersAccumulator
    },
    {} as SAFilter
  )

  // From the hardcoded list, getting the Filters that are not returned by the search provider, but they are required in the page
  const hardcodedFilters = Object.entries(hardcodedFacets).reduce(
    (
      hardcodedFiltersAccumulator,
      [
        facetName,
        { title, inputType, allowedValues, isOpen, isToggle, useHardcodedValues, extraData },
      ]
    ) => {
      const facetNameArray = facetName.split(`${selectedCategory}__`)
      const possibleCategoryValues = ALLOWED_CATEGORY_VALUES.map(({ value }) => slugify(value))
      const isFacetForSpecificCategory = possibleCategoryValues.some((categorySlugified) =>
        facetName.includes(`${categorySlugified}__`)
      )
      const facetNameString = facetNameArray[0] === '' ? facetNameArray[1] : facetNameArray[0]
      const shouldIncludeHardcodedFacet = isFacetForSpecificCategory
        ? useHardcodedValues && facetNameArray[0] === ''
        : useHardcodedValues
      const selectedFacetValues = selectedFacets[facetName] || []
      const hasValuesSelectedInFacet = selectedFacetValues.length > 0

      const orderedAllowedHardcodedOptions = allowedValues.reduce(
        (orderedArrayOfOptions, { label, value }) => {
          const isValueSelected = selectedFacetValues.includes(value)
          if (isValueSelected) {
            return [{ label, value }, ...orderedArrayOfOptions]
          }
          return [...orderedArrayOfOptions, { label, value }]
        },
        [] as ReadonlyArray<AllowedValueObject>
      )

      if (shouldIncludeHardcodedFacet) {
        hardcodedFiltersAccumulator[facetNameString] = {
          displayName: title,
          inputType,
          options: orderedAllowedHardcodedOptions,
          isOpen: hasValuesSelectedInFacet || isOpen,
          isToggle,
          extraData,
        }
      }

      return hardcodedFiltersAccumulator
    },
    {} as SAFilter
  )

  const mergedFacets = { ...saFiltersAllowed, ...hardcodedFilters }

  const finalFilters = facetsSpecialRules({
    facets,
    hardcodedFacets,
    selectedCategory,
    mergedFacets,
    selectedFacets,
  })

  return finalFilters
}

export const orderAndIsOpenFiltersByHardcodedList = ({
  allowedFilters,
  selectedCategory,
  hardcodedFacetsOrderAndIsOpen,
  selectedFacets,
}: OrderAndIsOpenFiltersByHardcodedListParams): SAFilter => {
  const categoryConfiguration =
    hardcodedFacetsOrderAndIsOpen[selectedCategory] || hardcodedFacetsOrderAndIsOpen['default']

  // Se não houver configuração para a categoria, retorna os filtros como estão
  if (!categoryConfiguration) return allowedFilters

  const { order, isOpen } = categoryConfiguration

  // If isOpen is empty, all facets are expanded
  const updatedEntries: Array<[string, SAFilterObject]> = Object.entries(allowedFilters).map(
    ([key, value]) => {
      const selectedFacetValues = selectedFacets[key] || []
      const hasValuesSelectedInFacet = selectedFacetValues.length > 0
      const isOpenValue = isOpen.length === 0 || isOpen.includes(key) || hasValuesSelectedInFacet
      return [key, { ...value, isOpen: isOpenValue }]
    }
  )

  // If order is empty, keep the order received
  if (order.length === 0) {
    return Object.fromEntries(updatedEntries)
  }

  // If facetName is not in the order array, put it at the end
  const sorted = updatedEntries.sort(([keyA], [keyB]) => {
    const indexA = order.indexOf(keyA)
    const indexB = order.indexOf(keyB)

    const weightA = indexA === -1 ? Infinity : indexA
    const weightB = indexB === -1 ? Infinity : indexB

    return weightA - weightB
  })

  return Object.fromEntries(sorted)
}

// We apply a transformation in the facets from the search provider to respect some specific rules we have in our application like: which facets should be visible, allowed values of each one, is it opened, the title, a custom action callback
export const transformFacetsToFilters = ({
  facets,
  nextAppRoute,
  baseUrl,
  facetService,
  selectedCategory = '',
  hardcodedFacets = {},
  hardcodedFacetsOrderAndIsOpen = {},
  handleToggleClick,
  selectedFacets,
}: TransformFacetsToFiltersParams): FilterCollapsableComponent => {
  // Getting 'facets' from service provider, but filtering options according to hardcoded list
  const allowedFilters = checkFacetsAllowed({
    facets,
    hardcodedFacets,
    selectedCategory,
    selectedFacets,
  })

  const orderedFilters = orderAndIsOpenFiltersByHardcodedList({
    allowedFilters,
    selectedCategory,
    hardcodedFacetsOrderAndIsOpen,
    selectedFacets,
  })

  return Object.entries(orderedFilters).map(
    ([facetName, { inputType, displayName, options, isOpen, isToggle, extraData }]) => ({
      title: displayName,
      content: filterComponent({
        facetName,
        inputType,
        options,
        nextAppRoute,
        baseUrl,
        facetService,
        extraData,
      }),
      isOpen: isToggle ? selectedFacets[facetName]?.includes('true') : isOpen,
      isToggle,
      onClick: isToggle
        ? (isInPathParam) => handleToggleClick?.({ isInPathParam, facetName, options })
        : undefined,
    })
  )
}
